import React from 'react';
import './status-flow.css';

/**
 * Custom glass status-flow editor — replaces the abandoned `react-flow-diagram`.
 * Each appointment status is a draggable node; arrows between nodes are the
 * allowed status transitions. Writes back the SAME model the save logic reads:
 *   status.draw = { id, type:'Task', width, height, x, y, linksTo:[{target}] }
 *   status.next = [targetStatusName, ...]
 *   status.hide = (no outgoing links)
 *
 * Props:
 *   statuses      array of the selected template's statuses
 *   selectedIndex index of the selected status (highlighted)
 *   onSelect(i)   click a node
 *   onChange(arr) emit the updated statuses array
 */
const NODE_W = 150;
const NODE_H = 60;
const GRID = 25;
const PAD = 16;

const snap = (v) => Math.round(v / GRID) * GRID;

export default function StatusFlow({ statuses = [], selectedIndex, onSelect, onChange }) {
  const canvasRef = React.useRef(null);
  const dragRef = React.useRef(null);          // { i, offX, offY, moved }
  const [, force] = React.useReducer((x) => x + 1, 0);
  const [drag, setDrag] = React.useState(null); // { i, x, y } transient position while dragging
  const [linkFrom, setLinkFrom] = React.useState(null); // index of node we're linking from

  // Resolve a node's position (fall back to a tidy row layout when unplaced).
  const posOf = (s, i) =>
    s.draw && typeof s.draw.x === 'number'
      ? { x: s.draw.x, y: s.draw.y }
      : { x: PAD + i * (NODE_W + 48), y: 60 };

  const livePos = (s, i) => (drag && drag.i === i ? { x: drag.x, y: drag.y } : posOf(s, i));

  const writeDraw = (arr) =>
    arr.map((s, i) => {
      const p = posOf(s, i);
      const next = s.next || [];
      return {
        ...s,
        next,
        hide: next.length === 0,
        draw: {
          id: s.name,
          type: 'Task',
          width: NODE_W,
          height: NODE_H,
          x: p.x,
          y: p.y,
          linksTo: next.map((t) => ({ target: t })),
        },
      };
    });

  const emit = (arr) => onChange && onChange(writeDraw(arr));

  // ---- node dragging ----
  const onNodeDown = (e, i) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const p = posOf(statuses[i], i);
    dragRef.current = {
      i,
      offX: e.clientX - rect.left - p.x,
      offY: e.clientY - rect.top - p.y,
      moved: false,
    };
    window.addEventListener('mousemove', onNodeMove);
    window.addEventListener('mouseup', onNodeUp);
  };
  const onNodeMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - d.offX);
    const y = Math.max(0, e.clientY - rect.top - d.offY);
    d.moved = true;
    // Keep the live position on the ref too — onNodeUp was attached at
    // mousedown, so its `drag` state closure is stale (always null).
    d.x = x;
    d.y = y;
    setDrag({ i: d.i, x, y });
  };
  const onNodeUp = () => {
    const d = dragRef.current;
    window.removeEventListener('mousemove', onNodeMove);
    window.removeEventListener('mouseup', onNodeUp);
    dragRef.current = null;
    if (!d) return;
    if (d.moved) {
      const cur = { x: d.x, y: d.y };
      const arr = statuses.map((s, k) =>
        k === d.i
          ? { ...s, draw: { ...(s.draw || {}), id: s.name, type: 'Task', width: NODE_W, height: NODE_H, x: snap(cur.x), y: snap(cur.y), linksTo: (s.next || []).map((t) => ({ target: t })) } }
          : s
      );
      setDrag(null);
      emit(arr);
    } else {
      // a click (no move): select or complete a link
      if (linkFrom != null && linkFrom !== d.i) {
        addLink(linkFrom, d.i);
        setLinkFrom(null);
      } else {
        onSelect && onSelect(d.i);
      }
    }
  };

  // ---- linking ----
  const addLink = (fromI, toI) => {
    const targetName = statuses[toI].name;
    const arr = statuses.map((s, k) => {
      if (k !== fromI) return s;
      const next = s.next ? [...s.next] : [];
      if (!next.includes(targetName)) next.push(targetName);
      return { ...s, next };
    });
    emit(arr);
  };
  const removeLink = (fromI, targetName) => {
    const arr = statuses.map((s, k) =>
      k === fromI ? { ...s, next: (s.next || []).filter((t) => t !== targetName) } : s
    );
    emit(arr);
  };

  // edge geometry: source right-middle -> target left-middle
  const edges = [];
  statuses.forEach((s, i) => {
    (s.next || []).forEach((targetName) => {
      const ti = statuses.findIndex((x) => x.name === targetName);
      if (ti < 0) return;
      const a = livePos(s, i);
      const b = livePos(statuses[ti], ti);
      const x1 = a.x + NODE_W;
      const y1 = a.y + NODE_H / 2;
      const x2 = b.x;
      const y2 = b.y + NODE_H / 2;
      edges.push({ fromI: i, targetName, x1, y1, x2, y2, mx: (x1 + x2) / 2, my: (y1 + y2) / 2 });
    });
  });

  // canvas size: fit all nodes
  let maxX = 600, maxY = 240;
  statuses.forEach((s, i) => {
    const p = livePos(s, i);
    maxX = Math.max(maxX, p.x + NODE_W + PAD);
    maxY = Math.max(maxY, p.y + NODE_H + PAD);
  });

  // New node lands on the grid below the existing ones (or top-left if empty).
  const addStatus = () => {
    let name = 'New status';
    let n = 2;
    while (statuses.some((s) => s.name === name)) name = 'New status ' + n++;
    const x = PAD + (statuses.length % 4) * (NODE_W + 48);
    const y = 40 + Math.floor(statuses.length / 4) * (NODE_H + 60);
    const arr = [...statuses, {
      name,
      next: [],
      hide: true,
      draw: { id: name, type: 'Task', width: NODE_W, height: NODE_H, x: snap(x), y: snap(y), linksTo: [] },
    }];
    emit(arr);
    onSelect && onSelect(arr.length - 1);
  };

  return (
    <div className="sf-wrap">
      <div className="sf-hint">
        {linkFrom != null
          ? <span className="sf-linking"><i className="fas fa-link"></i> Click a target status to connect — or click here to cancel</span>
          : <span><i className="fas fa-info-circle"></i> Drag to arrange · use a node’s <i className="fas fa-exchange-alt"></i> to link the flow · click an arrow’s ✕ to remove</span>}
        {linkFrom != null && <button className="sf-cancel" onClick={() => setLinkFrom(null)}>Cancel</button>}
        <button className="sf-add" onClick={addStatus}><i className="fas fa-plus"></i> Add status</button>
      </div>
      <div
        ref={canvasRef}
        className={`sf-canvas ${linkFrom != null ? 'sf-linking-mode' : ''}`}
        style={{ width: maxX, height: maxY }}
        onMouseDown={() => { if (linkFrom != null) setLinkFrom(null); }}
      >
        <svg className="sf-edges" width={maxX} height={maxY}>
          <defs>
            <marker id="sf-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L8,3 L0,6 Z" fill="#2563EB" />
            </marker>
          </defs>
          {edges.map((e, k) => {
            const c = Math.max(40, Math.abs(e.x2 - e.x1) / 2);
            return (
              <path
                key={k}
                d={`M ${e.x1} ${e.y1} C ${e.x1 + c} ${e.y1}, ${e.x2 - c} ${e.y2}, ${e.x2} ${e.y2}`}
                className="sf-edge"
                markerEnd="url(#sf-arrow)"
              />
            );
          })}
        </svg>

        {edges.map((e, k) => (
          <button
            key={'x' + k}
            className="sf-edge-x"
            style={{ left: e.mx - 9, top: e.my - 9 }}
            title="Remove transition"
            onClick={(ev) => { ev.stopPropagation(); removeLink(e.fromI, e.targetName); }}
          >×</button>
        ))}

        {statuses.map((s, i) => {
          const p = livePos(s, i);
          const accent = s.color || '#2563EB';
          return (
            <div
              key={i}
              className={`sf-node ${selectedIndex === i ? 'sel' : ''} ${linkFrom === i ? 'src' : ''}`}
              style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H, ['--sf-accent']: accent }}
              onMouseDown={(e) => onNodeDown(e, i)}
            >
              <i className={`sf-node-ico ${s.icon || 'fas fa-circle'}`}></i>
              <span className="sf-node-name">{s.name || 'Untitled'}</span>
              <button
                className="sf-handle"
                title="Link this status to another"
                onMouseDown={(e) => { e.stopPropagation(); setLinkFrom(i); }}
              ><i className="fas fa-exchange-alt"></i></button>
            </div>
          );
        })}

        {statuses.length === 0 && (
          <div className="sf-empty">No statuses yet — add one below to start building the flow.</div>
        )}
      </div>
    </div>
  );
}
